import React, { useMemo } from "react";
import PropTypes from "prop-types";

import UserService from "modules/user/service/UserService";
import DoctorCategory from "constants/type/user/DoctorCategory";
import SIDEBAR_ICONS from "../helper/sidebarIcons";

/**
 * The patient-screen left nav. Source: Figma "Sidebar · Expanded" (3015:4952),
 * read exactly via Dev Mode MCP. Three groups: Quick access, Medical history,
 * Lifestyle. Lifestyle rows carry a completion ring instead of an icon badge.
 *
 * Icons are the real Figma vectors, rendered as raw SVG so they theme via
 * currentColor. They come from a LATER board than the layout above — "Sidebar ·
 * live reference" (6189:4012) — see helper/sidebarIcons.js.
 *
 * COLLAPSING: the toggle in the top bar folds the panel to a rail holding only
 * that button, so it can always be reopened. The button existed before this and
 * was wired to nothing — `onCollapse` was never passed by PatientShell.
 */

/**
 * icon/panel-toggle/expanded (18x18, node 359:54), taken from the Dev Mode MCP
 * export of the sidebar board 6189:4012 — a 14.4x11.4 rounded outline, a
 * divider rule at x=7.2, and a chevron. The geometry that used to be here was
 * hand-approximated and measurably wrong on all three parts (a 16x13 box at
 * rx=2 rather than 14.4x11.4 at rx=1.2, the rule at x=8 rather than 7.2, and
 * every chevron point 0.8-1.3px out); these are Figma's own numbers.
 *
 * Only the chevron flips between states, and only the expanded state exists in
 * the board — the collapsed one is that same chevron mirrored about its own
 * axis (x=11.475), so the two point outward and inward symmetrically.
 *
 * linecap/linejoin sit on the paths rather than the root because that is where
 * Figma puts them; the rect is a closed rounded shape and carries neither.
 */
const PanelToggle = ({ collapsed }) => (
  <svg
    className="ps-toggle-icon"
    viewBox="0 0 18 18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    aria-hidden="true"
  >
    <rect x="2.3" y="4.3" width="14.4" height="11.4" rx="1.2" />
    <path d="M7.2 3.5V16.5" strokeLinecap="round" strokeLinejoin="round" />
    {collapsed ? (
      <path d="M10.35 6.75L12.6 9L10.35 11.25" strokeLinecap="round" strokeLinejoin="round" />
    ) : (
      <path d="M12.6 6.75L10.35 9L12.6 11.25" strokeLinecap="round" strokeLinejoin="round" />
    )}
  </svg>
);
PanelToggle.propTypes = { collapsed: PropTypes.bool };
const Icon = ({ name }) => (
  <span
    className="ps-icon"
    aria-hidden="true"
    dangerouslySetInnerHTML={{ __html: SIDEBAR_ICONS[name] || "" }}
  />
);
Icon.propTypes = { name: PropTypes.string.isRequired };

/* Hardcoded nav model — matches the Figma frame. `icon` is the key into
   SIDEBAR_ICONS.

   The four Lifestyle rows used to carry a `pct` completion ring. Nothing ever
   computed it — every one was hardcoded 0 — so it read as "0% complete" on
   sections the clinician had actually filled in. Removed rather than wired up:
   there is no completion signal in any of these sections' payloads to derive
   it from. */
const GROUPS = [
  {
    label: "Quick access",
    items: [
      { key: "ai-transcript", name: "ECHO - AI transcript" },
      { key: "patient-details", name: "Patient details" },
      { key: "progress-notes", name: "Progress notes" },
    ],
  },
  {
    label: "Medical history",
    items: [
      { key: "anthropometric", name: "Anthropometric data" },
      { key: "chief-complaints", name: "Chief complaints" },
      { key: "diagnosis", name: "Diagnosis" },
      { key: "medicines", name: "Medicines" },
      { key: "lab-investigations", name: "Lab investigations" },
      { key: "allergies", name: "Allergies" },
      { key: "family-history", name: "Family history" },
      { key: "hospitalisation", name: "Hospitalisation history" },
      { key: "drug-schedule", name: "Drug schedule" },
    ],
  },
  {
    label: "Lifestyle",
    items: [
      { key: "food-preference", name: "Food preferences" },
      { key: "daily-schedule", name: "Daily schedule" },
      { key: "physical-activity", name: "Physical activity" },
      { key: "addictions", name: "Addictions" },
    ],
  },
];

/* Which clinician categories get the "ECHO - AI transcript" row. The AI
   transcript call runs in-browser via LiveKit for these two categories only;
   every other category (doctor / medical-team / psychotherapist / ops-only
   employee) is routed through SparkTG by /initiate-call and has no reason
   to see the row — it would just open a modal they can't use. Same category
   split the backend already applies for vendor selection. */
const AI_TRANSCRIPT_CATEGORIES = new Set([
  DoctorCategory.NUTRITIONIST,
  DoctorCategory.PHYSIOTHERAPIST,
]);

const PatientSidebar = ({ active, onSelect, collapsed, onToggle }) => {
  const groups = useMemo(() => {
    const category = new UserService().doctorCategory();
    if (AI_TRANSCRIPT_CATEGORIES.has(category)) return GROUPS;
    return GROUPS.map((group) =>
      group.label === "Quick access"
        ? { ...group, items: group.items.filter((it) => it.key !== "ai-transcript") }
        : group,
    );
  }, []);

  return (
  <nav
    className={`patient-sidebar${collapsed ? " is-collapsed" : ""}`}
    aria-label="Patient sections"
  >
    <div className="ps-head">
      {!collapsed && <span className="ps-head-label">Quick access</span>}
      <button
        type="button"
        className="ps-collapse"
        aria-label={collapsed ? "Expand quick access" : "Collapse quick access"}
        aria-expanded={!collapsed}
        title={collapsed ? "Expand quick access" : "Collapse quick access"}
        onClick={onToggle}
      >
        <PanelToggle collapsed={collapsed} />
      </button>
    </div>

    {/* Collapsed = Figma "Sidebar · Collapsed (icons only)" (1944:1843): a 64px
        rail of 40x40 cells. Same rows, same active state, same click targets —
        the label and the completion ring are dropped, the count is not. */}
    {groups.map((group, gi) => (
      <div className="ps-group" key={group.label}>
        {/* The first group's label is rendered in the head, next to the collapse
            control, so skip repeating it. */}
        {gi > 0 && !collapsed && <span className="ps-group-label">{group.label}</span>}
        {group.items.map((item) => {
          const isActive = item.key === active;
          return (
            <button
              key={item.key}
              type="button"
              className={`ps-row${isActive ? " is-active" : ""}`}
              aria-current={isActive ? "page" : undefined}
              // The name is the only thing identifying a row once the label is
              // hidden, so it becomes the tooltip and the accessible name.
              title={collapsed ? item.name : undefined}
              aria-label={collapsed ? item.name : undefined}
              onClick={() => onSelect && onSelect(item.key)}
            >
              <span className="ps-row-icon">
                <Icon name={item.key} />
              </span>
              {!collapsed && <span className="ps-row-label">{item.name}</span>}
              {/* The rail keeps the count, as a small pill pinned to the icon's
                  top-right (1944:1850) — a different treatment AND a different
                  red from the expanded panel's disc. */}
              {item.badge != null && (
                <span className={collapsed ? "ps-rail-badge" : "ps-badge"}>{item.badge}</span>
              )}
            </button>
          );
        })}
      </div>
    ))}
  </nav>
  );
};

PatientSidebar.propTypes = {
  /** key of the active section, e.g. "anthropometric". */
  active: PropTypes.string,
  onSelect: PropTypes.func,
  collapsed: PropTypes.bool,
  onToggle: PropTypes.func,
};

export default PatientSidebar;
